import { amountFormatter } from './helper'
import { DEFAULT_ALLOWED_SLIPPAGE, SupportedNetWork } from './constants'
import { addressPreCheck, formatBigNumberToAmount, paraErrorMessageFormatter } from './helper'
import BigNumber from 'bignumber.js'
import { ethers } from 'ethers'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import set from 'lodash/set'
import { NetworkConfig } from './types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  getBestPrice,
  getSourceDestTokenByNetwork,
  getNativeBalance,
  getTokenBalance,
  paraController,
} from './swapUtils'
import {
  usePublicClient,
  useAccount,
  useChainId,
  useConfig,
  useSwitchChain,
  Config as WagmiConfig,
} from 'wagmi'
import type { Address } from 'viem'
import { useWalletService } from '../hooks/useWalletService'
import { LiquidationAsset, TargetCoinLiquidation, TypeLiquidation } from './types'
import { onGetTokensFromCustomLiquidation } from './utils'
import { OptimalRate } from './paraswap-types'
import { HttpProvider } from 'web3-providers-http'
import { getRefCode } from '../utils/localStorage'
import {trackRevenue, trackSwapSellAllWithRefcode} from '../services/analyticsService'
import { AccountController } from '../controller/AccountController'
import { SolanaWalletAdapter, SuiWalletAdapter } from '../controller/AccountController/BaseAccountController'
import { WalletType } from '../types/wallet'

// CMC API function with 1-hour localStorage cache
const fetchCMCTokenPrices = async (limit: number = 1000): Promise<any> => {
  const CACHE_KEY = `cmc_list_limit`
  const ONE_HOUR_MS = 60 * 60 * 1000

  try {
    // Return cached if available and not expired
    if (typeof window !== 'undefined') {
      const cachedRaw = localStorage.getItem(CACHE_KEY)
      if (cachedRaw) {
        try {
          const cached = JSON.parse(cachedRaw) as { data: any; timestamp: number }
          if (cached && cached.timestamp && Date.now() - cached.timestamp < ONE_HOUR_MS) {
            return cached.data
          }
        } catch (_) {}
      }
    }

    const response = await fetch('https://api.blockchain-infos.com/api/v1/cmcs-v2?limit=' + limit, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`CMC API error: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()

    // Cache successful response
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ data: data.data, timestamp: Date.now() }))
      }
    } catch (_) {}

    return data.data
  } catch (error) {
    try {
      if (typeof window !== 'undefined') {
        const cachedRaw = localStorage.getItem(CACHE_KEY)
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw) as { data: any }
          return cached.data
        }
      }
    } catch (_) {}
  }
}

type UseCoinLiquidationOptions = {
  walletType?: WalletType
  solanaWallet?: SolanaWalletAdapter | null
  solanaRpcUrl?: string
  suiWallet?: SuiWalletAdapter | null
  suiRpcUrl?: string
  suiSwapApiUrl?: string
}

const useCoinLiquidation = (userAddress?: string, options: UseCoinLiquidationOptions = {}) => {
  const walletType = options.walletType || 'EVM'
  const [typeLiquidation, setTypeLiquidation] = useState(TypeLiquidation.CUSTOM)
  const [targetCoin, setTargetCoin] = useState(TargetCoinLiquidation.USDC)
  const [globalLiquidationPercentage, setGlobalLiquidationPercentage] = useState(1)
  const [globalLiquidationData, setGlobalLiquidationData] = useState<Record<string, unknown>>({})
  const [customLiquidation, setCustomLiquidation] = useState<Record<string, unknown>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [receivedTotal, setReceivedTotal] = useState(0)

  // Wagmi hooks - single source of truth for wallet
  const { address: wagmiAddress, connector } = useAccount()
  const chainId = useChainId()
  // console.log("**** chainId", chainId)
  // console.log("**** isConnected", isConnected)

  const publicClient = usePublicClient()
  const config: WagmiConfig = useConfig()
  const { switchChainAsync } = useSwitchChain()
  const walletService = useWalletService()

  // Use wagmi address as primary, fallback to userAddress
  const account =
    walletType === 'SOL' || walletType === 'SUI'
      ? userAddress || ''
      : wagmiAddress || userAddress || ''

  // Mock wallet coin state - replace with your actual wallet state management
  const [walletCoinState] = useState({
    tokenByNetwork: {} as Record<string, LiquidationAsset[]>,
  })

  const buildDataForGlobalLiquidation = useCallback(() => {
    const customData: Record<string, unknown> = {}
    SupportedNetWork.forEach((network: any) => {
      const tokens = (walletCoinState?.tokenByNetwork?.[network.chainId] ?? [])
        .filter((e: LiquidationAsset) => Number(e.USDBalance) > 0)
        ?.sort((a: LiquidationAsset, b: LiquidationAsset) => a.symbol.localeCompare(b.symbol))

      const totalBalanceInNetwork = tokens?.reduce(
        (total: number, coin: LiquidationAsset) => total + coin.USDBalance,
        0,
      )

      set(customData, `${network.chainId}.name`, network.name)

      tokens.forEach((token: LiquidationAsset) => {
        const allocation =
          (totalBalanceInNetwork ? Number(token.USDBalance) / totalBalanceInNetwork : 0) *
          (globalLiquidationPercentage / 100)

        set(customData, `${network.chainId}.${token.symbol}`, {
          ...token,
          allocation,
          isSelected: true,
        })
      })
    })

    setGlobalLiquidationData((prevData) => ({ ...prevData, ...customData }))

    return customData
  }, [globalLiquidationPercentage, walletCoinState.tokenByNetwork])

  useEffect(() => {
    if (isEmpty(globalLiquidationData)) {
      buildDataForGlobalLiquidation()
    }
  }, [buildDataForGlobalLiquidation, globalLiquidationData])

  const onShowLoading = (field: string, loading: boolean) => {
    const customData =
      typeLiquidation === TypeLiquidation.GLOBAL ? globalLiquidationData : customLiquidation

    const currentCoin = get(customData, field, {}) as LiquidationAsset
    const newCustomLiquidation =
      set(customData, field, {
        ...currentCoin,
        loading,
      }) || {}

    if (typeLiquidation === TypeLiquidation.GLOBAL) {
      setGlobalLiquidationData((prevCustomLiquidation: Record<string, unknown>) => ({
        ...prevCustomLiquidation,
        ...newCustomLiquidation,
      }))
    } else {
      setCustomLiquidation((prevCustomLiquidation: Record<string, unknown>) => ({
        ...prevCustomLiquidation,
        ...newCustomLiquidation,
      }))
    }
  }

  const onHandleError = useCallback((field: string, error: any) => {
    const customData =
      typeLiquidation === TypeLiquidation.GLOBAL ? globalLiquidationData : customLiquidation

    const currentCoin = get(customData, field, {}) as LiquidationAsset
    const newCustomLiquidation =
      set(customData, field, {
        ...currentCoin,
        error: paraErrorMessageFormatter(
          error?.message || error,
          currentCoin.network,
          error?.message || error,
        ),
      }) || {}

    if (typeLiquidation === TypeLiquidation.GLOBAL) {
      setGlobalLiquidationData((prevCustomLiquidation: Record<string, unknown>) => ({
        ...prevCustomLiquidation,
        ...newCustomLiquidation,
      }))
    } else {
      setCustomLiquidation((prevCustomLiquidation: Record<string, unknown>) => ({
        ...prevCustomLiquidation,
        ...newCustomLiquidation,
      }))
    }
  }, [customLiquidation, globalLiquidationData, typeLiquidation])

  const onSelectCoin = useCallback(
    (network: NetworkConfig, coin?: LiquidationAsset) => {
      const networkChainId = network.chainId
      if (!coin) {
        const isSelected = get(customLiquidation, `${networkChainId}.isSelected`, false)
        setCustomLiquidation((prevCustomLiquidation: Record<string, unknown>) => ({
          ...prevCustomLiquidation,
          ...{
            [networkChainId]: {
              ...(get(customLiquidation, networkChainId, {}) as Record<string, unknown>),
              name: network.name,
              isSelected: !isSelected,
            },
          },
        }))
        return
      }

      const isSelected = get(
        customLiquidation,
        `${networkChainId}.${coin.symbol}.isSelected`,
        false,
      )

      const newCustomLiquidation =
        set(customLiquidation, `${networkChainId}.${coin.symbol}`, {
          ...(get(customLiquidation, `${networkChainId}.${coin.symbol}`, {}) as LiquidationAsset),
          ...coin,
          isSelected: !isSelected,
          loading: true,
        }) || {}

      setCustomLiquidation((prevCustomLiquidation: Record<string, unknown>) => ({
        ...prevCustomLiquidation,
        ...newCustomLiquidation,
      }))
    },
    [customLiquidation],
  )

  const onChangeCoinPrice = debounce((coin: LiquidationAsset, price: string) => {
    const networkChainId = coin.network.chainId
    const coinSymbol = coin.symbol

    const currentCoin = get(
      customLiquidation,
      `${networkChainId}.${coinSymbol}`,
      {},
    ) as LiquidationAsset
    const newCustomLiquidation =
      set(customLiquidation, `${networkChainId}.${coinSymbol}`, {
        ...currentCoin,
        price,
        error: undefined,
      }) || {}

    setCustomLiquidation((prevCustomLiquidation: Record<string, unknown>) => ({
      ...prevCustomLiquidation,
      ...newCustomLiquidation,
    }))
  }, 200)

  const getSourceAmountByLiquidationType = async ({
    inputToken,
    network,
  }: {
    inputToken: LiquidationAsset
    network: NetworkConfig
  }) => {
    const { balance } = await getTokenBalance({
      userAddress: account,
      token: inputToken,
    })

    const balanceAmount = balance
      ? `${amountFormatter(
          balance ? ethers.BigNumber.from(balance) : ethers.constants.Zero,
          inputToken?.decimals,
          undefined,
          false,
        )}`
      : '0'

    const balanceString =
      addressPreCheck(inputToken?.address.toLowerCase()) === 'native'
        ? (Number(balanceAmount) - Number('0.002')).toFixed(8)
        : balanceAmount

    const amount =
      typeLiquidation === TypeLiquidation.GLOBAL
        ? (globalLiquidationPercentage / 100) * Number(inputToken.USDBalance)
        : Number(inputToken.price)

    // If USDBalance is 0, use the full balance
    if (Number(inputToken.USDBalance) === 0) {
      return balanceString
    }

    const sourceAmount = inputToken.price
      ? (amount * Number(balanceString)) /
        (Number(inputToken.USDBalance) || Number(inputToken.stringUSDBalance))
      : balanceString

    if (inputToken.isNative) {
      const usdValue = Number(inputToken.USDBalance) || 0
      let lockAmount = 0.5
      try {
        if (typeof window !== 'undefined') {
          const saved = localStorage.getItem('native_lock_usd')
          const parsed = saved ? Number(saved) : 0.5
          lockAmount = isNaN(parsed) || parsed < 0 ? 0.5 : parsed
        }
      } catch (_) {}
      const availableAmount = usdValue - lockAmount

      if (availableAmount <= 0) {
        onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientAmount')
        return
      }

      // Calculate the actual amount to sell (keeping $0.5 locked)
      const sellPercentage = availableAmount / usdValue
      const actualSellAmount = Number(sourceAmount) * sellPercentage

      return actualSellAmount.toFixed(8)
    }

    // Validate sourceAmount
    if (!sourceAmount || Number(sourceAmount) <= 0) {
      onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientAmount')
      return
    }

    if (Number(inputToken.price || inputToken.USDBalance) > Number(inputToken.USDBalance)) {
      onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientAmount')
      return
    }

    return sourceAmount + ''
  }

  const onSignContractWithWagmi = async ({
    inputToken,
    outputToken,
    network,
    priceRoute,
    sourceAmount,
  }: {
    inputToken: any
    outputToken: any
    network: NetworkConfig
    priceRoute: OptimalRate
    sourceAmount: string
  }) => {
    try {
      // const destAmount = formatBigNumberToAmount(
      //   priceRoute?.destAmount,
      //   outputToken?.decimals,
      // ).toFixed()

      const minDestinationAmount = new BigNumber(priceRoute!.destAmount).multipliedBy(
        1 - DEFAULT_ALLOWED_SLIPPAGE,
      )

      const txParams = await paraController.buildSwap(
        inputToken as any,
        outputToken,
        priceRoute!.srcAmount,
        minDestinationAmount.toFixed(0),
        account,
      )

      // Use wallet service for swap transaction
      if (!walletService) {
        throw new Error('Wallet service not available')
      }

      const chain = config.chains.find((chain) => chain.id === network.chainId) || config.chains[0]

      const swapHash = await walletService.sendTransaction({
        account: wagmiAddress as Address,
        to: txParams.to as Address,
        value: BigInt(txParams.value || '0'),
        data: txParams.data as `0x${string}`,
        gas: txParams.gas ? BigInt(txParams.gas) : undefined,
        chain,
      })

      // Wait for transaction receipt
      if (publicClient) {
        const destAmount = formatBigNumberToAmount(
          priceRoute?.destAmount,
          outputToken?.decimals,
        ).toFixed()

        setReceivedTotal((prevTotal) => prevTotal + Number(priceRoute?.destUSD || 0))

        // Return swap result data for buyback tracking
        return {
          success: true,
          txHash: swapHash,
          inputToken,
          outputToken,
          network,
          sourceAmount,
          destAmount,
          srcUSD: Number(priceRoute?.srcUSD || 0),
          destUSD: Number(priceRoute?.destUSD || 0),
        }

        // Don't dispatch success event here - wait for all tokens to complete
        // Success will be handled in onSellAllWithCustomLiquidation after all swaps
      }

      return { success: false }
    } catch (swapError) {
      return { success: false }
    }
  }

  const onSwapToken = async ({
    sourceToken,
    network,
  }: {
    sourceToken: LiquidationAsset
    network: NetworkConfig
  }) => {
    try {
      if (
        network.chainId !== paraController.network ||
        ![network.fallBack, network.rpc].includes(
          (paraController.web3.currentProvider as HttpProvider)?.host,
        )
      ) {
        paraController.onChangeNetwork(network)
      }
      const { inputToken, outputToken }: any = await getSourceDestTokenByNetwork(
        network.chainId,
        sourceToken,
        targetCoin,
      )

      // console.log("**** inputToken, outputToken", inputToken, outputToken)
      // return
      if (!outputToken) {
        return
      }

      const sourceAmount = await getSourceAmountByLiquidationType({
        inputToken,
        network,
      })

      if (!sourceAmount) {
        return
      }

      const { priceRoute, bnbCost } = await getBestPrice({
        amount: sourceAmount,
        inputToken,
        outputToken,
        userAddress: account,
        onHandleError,
      })

      if (!priceRoute) {
        return
      }

      const { allowance, balance } = await getTokenBalance({
        userAddress: account,
        token: inputToken,
        spender: priceRoute?.tokenTransferProxy,
      })

      // Check if allowance is sufficient (allowance >= required amount)
      // Handle case where allowance might be '0' or actual amount
      const isAllowanceSufficient =
        allowance &&
        allowance !== '0' &&
        ethers.BigNumber.from(allowance).gte(ethers.BigNumber.from(priceRoute.srcAmount))

      const nativeBalance = await getNativeBalance(account)

      const currFormat = amountFormatter(
        balance ? ethers.BigNumber.from(balance) : ethers.constants.Zero,
        inputToken.decimals,
        undefined,
        false,
      )

      const amountFormat = amountFormatter(
        priceRoute.srcAmount !== '0'
          ? ethers.BigNumber.from(priceRoute.srcAmount)
          : ethers.constants.Zero,
        inputToken.decimals,
        undefined,
        false,
      )

      if (network?.symbol === inputToken?.symbol) {
        if (Number(currFormat ?? 0) < Number(amountFormat) + Number(bnbCost)) {
          onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientFunds')
          return
        }
      } else {
        if (nativeBalance < Number(bnbCost)) {
          onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientFunds')
          return
        }
      }

      const isWalletConnect = connector?.type === 'walletConnect'
      if (isWalletConnect) {
        if (chainId !== inputToken.network.chainId) {
          try {
            await switchChainAsync({ chainId: inputToken.network.chainId })
            await new Promise((resolve) => setTimeout(resolve, 3000))
          } catch (switchError) {
            // WalletConnect chain switch failed
          }
        }
      } else {
        if (chainId !== inputToken.network.chainId) {
          try {
            await switchChainAsync({ chainId: inputToken.network.chainId })
            await new Promise((resolve) => setTimeout(resolve, 2000))
          } catch (switchError) {
            // Failed to switch chain
          }
        }
      }

      if (!isAllowanceSufficient) {
        try {
          if (!walletService) {
            throw new Error('Wallet service not available')
          }

          // 2. Validate spender address
          if (
            !priceRoute.tokenTransferProxy ||
            priceRoute.tokenTransferProxy === '0x0000000000000000000000000000000000000000'
          ) {
            throw new Error('Invalid spender address')
          }

          // 3. Use wallet service for approval
          const chain =
            config.chains.find((chain) => chain.id === inputToken.network.chainId) ||
            config.chains[0]

          const approvalHash = await walletService.approveToken({
            tokenAddress: inputToken.address as Address,
            spenderAddress: priceRoute.tokenTransferProxy as Address,
            amount: BigInt(priceRoute.srcAmount),
            account: wagmiAddress as Address,
            chain,
          })

          console.log('approvalHash', approvalHash)
          // 4. Wait for approval transaction confirmation
          await new Promise((resolve) => setTimeout(resolve, 10000))

          // 5. After approval, execute swap using Wagmi (not signAndSendRawTransaction)
          const result = await onSignContractWithWagmi({
            inputToken,
            network,
            outputToken,
            priceRoute,
            sourceAmount,
          })

          return result
        } catch (approvalError) {
          // Failed to approve
        }
        return { success: false }
      }
      const result = await onSignContractWithWagmi({
        inputToken,
        network,
        outputToken,
        priceRoute,
        sourceAmount,
      })

      return result
    } catch (error: any) {
      const message = paraErrorMessageFormatter(
        error?.message || error,
        network,
        error?.message || error,
      )
      onHandleError(`${network.chainId}.${sourceToken.symbol}`, message)
    }
  }

  const controllerConfig = useMemo(
    () => ({
      chainId: chainId,
      walletService: walletService,
      wagmiConfig: config,
      connector: connector,
      account: account,
      publicClient: publicClient,
      typeLiquidation: typeLiquidation,
      globalLiquidationPercentage: globalLiquidationPercentage,
      onHandleError: onHandleError,
      targetCoin: targetCoin,
      switchChainAsync: switchChainAsync,
      solanaWallet: options.solanaWallet || undefined,
      solanaRpcUrl: options.solanaRpcUrl,
      suiWallet: options.suiWallet || undefined,
      suiRpcUrl: options.suiRpcUrl,
      suiSwapApiUrl: options.suiSwapApiUrl,
    }),
    [
      account,
      chainId,
      config,
      connector,
      globalLiquidationPercentage,
      onHandleError,
      publicClient,
      switchChainAsync,
      targetCoin,
      typeLiquidation,
      walletService,
      options.solanaRpcUrl,
      options.solanaWallet,
      options.suiRpcUrl,
      options.suiSwapApiUrl,
      options.suiWallet,
    ],
  )

  useEffect(() => {
    if (!account) return
    if (walletType === 'EVM' && connector) {
      AccountController.self().updateConfig(controllerConfig)
    }
    if (walletType === 'SOL' && controllerConfig.solanaWallet) {
      AccountController.self().updateConfig(controllerConfig)
    }
    if (walletType === 'SUI' && controllerConfig.suiWallet) {
      AccountController.self().updateConfig(controllerConfig)
    }
  }, [
    walletType,
    account,
    connector,
    controllerConfig,
  ])
  useEffect(() => {
    if (!account) return
    if (walletType === 'EVM' && connector) {
      AccountController.self().onChangeAccount(account, 'EVM', controllerConfig)
    }
    if (walletType === 'SOL' && controllerConfig.solanaWallet) {
      AccountController.self().onChangeAccount(account, 'SOL', controllerConfig)
    }
    if (walletType === 'SUI' && controllerConfig.suiWallet) {
      AccountController.self().onChangeAccount(account, 'SUI', controllerConfig)
    }
  }, [
    walletType,
    account,
    connector,
    controllerConfig,
  ])
  const onSellAllWithCustomLiquidation = async () => {
    try {
      setIsLoading(true)
      const tokens = onGetTokensFromCustomLiquidation(customLiquidation)
      const refCode = getRefCode()
      const responses = []
      const buybackItems: any[] = []
      let successCount = 0
      let totalTokens = tokens.length

      await AccountController.self().sellAll(tokens, (info) => {
        if (info.response) {
          const response = info.response
          responses.push(response)

          const findToken = tokens.find(e => e.symbol === response.inputToken?.symbol)
          if (findToken) {
            trackRevenue(response?.txHash, refCode, findToken.USDBalance, 'sell', AccountController.self().type)
          }
        }
        if (info.buybackItem) {
          buybackItems.push(info.buybackItem)
          successCount++
        }
        if (info.loadingMessage) {
          onShowLoading(`${info.loadingMessage}`, false)
        }
      })

      // Only dispatch success event if at least one token was successfully swapped
      if (successCount > 0) {
        try {
          const refCode = getRefCode()
          if (refCode) {
            const swapAmount = buybackItems?.reduce(
              (sum: number, item: any) => sum + (item.soldAmountUSD || 0),
              0,
            )
            trackSwapSellAllWithRefcode(
              refCode,
              swapAmount,
              walletType === 'SOL' ? 'sol' : walletType === 'SUI' ? 'sui' : 'evm',
            )
            console.log('after done', responses, tokens)
          }

          window.dispatchEvent(
            new CustomEvent('swap:success', {
              detail: {
                successCount,
                totalTokens,
                tokens: tokens.map((t) => ({ symbol: t.symbol, network: t.network.name })),
                buybackItems,
              },
            }),
          )
        } catch (_) {}
      }

      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
    }
  }

  const onSellAll = () => {
    onSellAllWithCustomLiquidation()
  }

  return {
    isLoading: isLoading,
    typeLiquidation,
    targetCoin,
    globalLiquidationPercentage,
    globalLiquidationData,
    customLiquidation,
    receivedTotal,
    setTypeLiquidation,
    setTargetCoin,
    setGlobalLiquidationPercentage,
    setGlobalLiquidationData,
    setCustomLiquidation,
    setReceivedTotal,
    onSelectCoin,
    onChangeCoinPrice,
    getBestPrice,
    onSwapToken,
    onHandleError,
    onSellAll,
    fetchCMCTokenPrices,
  }
}

export default useCoinLiquidation
